// Pure helpers for the Decode tool on the Packets page (pages/DecodePage.tsx); no imports so
// tests/decodeTool.test.mjs can run it directly. The server (POST /api/decode/meshcore) does the decoding;
// this turns its answer into what the page shows and its errors into one plain line.

export interface DecodeSummaryRow {
  label: string;
  value: string;
}

export interface DecodeResponse {
  length: number;
  hex: string;
  wire_hash: string;
  summary: DecodeSummaryRow[];
  fields: { start: number; end: number; name: string; group: "header" | "route" | "address" | "check" | "content" | "other"; value: string }[];
  message: { channel: string; sender: string | null; text: string; sent_at: string } | null;
  stored: { id: string | number } | null;
}

// A real advert captured off the air (the same packet the backend tests decode).
export const EXAMPLE_ADVERT_HEX =
  "12000ECF16D57F8612F93FDD7100F3711610B0ED6994F6BE492BD5C4B59CC16764D13018AF6AF479562E36BAA9EB444ABD241A0BB3452D49C69" +
  "D0DF4C79839F2DCE9BAE27A1EC17289633CE548AE990DFE60D54612DD5D7DB9FC253707D996020B998AAC910E9233DC6F02B9BA54F94C6F6E67" +
  "76696C6C65205265706561746572";

/** True when there is nothing to decode yet (only blanks and separators). */
export function isBlank(text: string): boolean {
  return text.replace(/[\s:,;]/g, "").replace(/0x/gi, "") === "";
}

/**
 * One plain line for a failed decode. The server's own wording (bad hex, odd length, too long, not a MeshCore
 * packet) comes through as it is; a lost session or a missing tool gets its own sentence.
 */
export function errorText(status: number | undefined, message: string): string {
  if (status === 401) return "Your session ended. Sign in again to decode.";
  if (status === 403) return "Your account can't use this tool.";
  if (status === 404) return "The Decode tool isn't available on this server.";
  if (status === 422 || status === 400) {
    const i = message.indexOf(" — ");
    const detail = i >= 0 ? message.slice(i + 3).trim() : "";
    return detail || "That could not be decoded.";
  }
  if (status === undefined) return "Couldn't reach the server. Try again.";
  return "The decode failed. Try again.";
}

/** The decoded channel message as one line, or null when it stayed encrypted. */
export function messageLine(m: DecodeResponse["message"]): string | null {
  if (!m) return null;
  return `${m.sender ? `${m.sender}: ` : ""}${m.text}`;
}

/** Where the channel message was read: "Channel #name, sent <time>". */
export function messageSource(m: NonNullable<DecodeResponse["message"]>): string {
  return `Channel ${m.channel}, sent ${m.sent_at}`;
}

/** A line about the message for packets that carry one but could not be opened; null for other types. */
export function encryptedNote(res: DecodeResponse): string | null {
  if (res.message) return null;
  const type = res.summary.find((r) => r.label === "Type")?.value;
  return type === "Channel message" ? "Encrypted. None of the channel keys we hold opens it." : null;
}
